/**
 * De opslagstatus, in gewone taal.
 *
 * "Opgeslagen" verschijnt uitsluitend nadat de server het bevestigd heeft.
 * Alle andere toestanden zeggen wat er aan de hand is én dat het werk niet
 * kwijt is -- dat is precies wat iemand op dat moment wil weten.
 */
import React from 'react';
import { AlertTriangle, Check, CloudOff, Loader2, PencilLine, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { SaveStatus } from '../types';

interface SaveIndicatorProps {
  status: SaveStatus;
  errorMessage: string | null;
  onRetry?: () => void;
}

const PRESENTATION: Record<
  SaveStatus,
  { label: string; icon: typeof Check; className: string; spin?: boolean }
> = {
  idle: { label: 'Alles opgeslagen', icon: Check, className: 'text-slate-500' },
  dirty: { label: 'Wijzigingen worden opgeslagen', icon: PencilLine, className: 'text-slate-500' },
  saving: { label: 'Opslaan…', icon: Loader2, className: 'text-slate-500', spin: true },
  saved: { label: 'Opgeslagen', icon: Check, className: 'text-emerald-700' },
  error: { label: 'Niet opgeslagen', icon: CloudOff, className: 'text-amber-700' },
  conflict: { label: 'Iemand anders bewerkte deze website', icon: Users, className: 'text-amber-700' },
};

export function SaveIndicator({ status, errorMessage, onRetry }: SaveIndicatorProps) {
  const presentation = PRESENTATION[status];
  const Icon = presentation.icon;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="sites-save-status"
      data-status={status}
      className={cn('flex items-center gap-2 text-xs font-semibold', presentation.className)}
    >
      <Icon className={cn('h-3.5 w-3.5', presentation.spin && 'animate-spin')} />
      <span className="hidden sm:inline">{presentation.label}</span>
      {status === 'error' && errorMessage ? (
        <span className="hidden items-center gap-1 text-xs font-normal text-slate-500 lg:inline-flex">
          <AlertTriangle className="h-3 w-3" />
          {errorMessage}
        </span>
      ) : null}
      {status === 'error' && onRetry ? <button type="button" onClick={onRetry} className="rounded-lg bg-amber-50 px-2 py-1 text-xs font-bold text-amber-900 transition hover:bg-amber-100">Opnieuw</button> : null}
    </div>
  );
}
